/**
 * Shared workforce → display summarizer. Resolves human labels (registry for
 * known roles, `entry.label` for custom roles), display group, and specialty
 * sub-item breakdowns into an ordered structure consumed by every place the
 * org workforce is serialized into LLM context (intake citation docs, intake
 * system prompt, ROI generation org-context). Pure — no I/O, no validation
 * (see `validateWorkforce` in ./workforce for that).
 */
import type { WorkforceEntry } from "./types";
import {
  getWorkforceRoleDef,
  getWorkforceRoleGroup,
  getWorkforceRoleLabel,
  WORKFORCE_GROUP_ORDER,
  WORKFORCE_ROLE_DEFS,
  type WorkforceGroup,
} from "./workforce-registry";

export interface WorkforceSubItemSummary {
  label: string;
  ftes: number;
  /** avg_fully_loaded_cost when present, else null. */
  cost: number | null;
}

export interface WorkforceRoleSummary {
  /** Stable role key (registry key or `custom_<slug>`) — used for fact field_paths. */
  key: string;
  /** entry.label (custom roles) ?? registry label ?? key. */
  label: string;
  group: WorkforceGroup;
  custom: boolean;
  ftes: number;
  cost: number;
  overtimePct?: number;
  subItems: WorkforceSubItemSummary[];
}

function finiteOrZero(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

const GROUP_ORDER_INDEX = new Map<WorkforceGroup, number>(
  WORKFORCE_GROUP_ORDER.map((group, index) => [group, index]),
);
const ROLE_ORDER_INDEX = new Map<string, number>(
  WORKFORCE_ROLE_DEFS.map((def, index) => [def.key, index]),
);

function isBlankTemplateRole(
  key: string,
  entry: WorkforceEntry,
  trimmedLabel: string | undefined,
  ftes: number,
  cost: number,
  subItems: WorkforceSubItemSummary[],
): boolean {
  const def = getWorkforceRoleDef(key);
  return (
    def?.isBenchmark === false &&
    entry.custom !== true &&
    trimmedLabel === undefined &&
    ftes === 0 &&
    cost === 0 &&
    subItems.length === 0
  );
}

/**
 * Resolve a workforce map into ordered, label-resolved role summaries. Order:
 * WORKFORCE_GROUP_ORDER, then registry order within group, custom/unknown roles
 * last (stable by key). Tolerates malformed runtime data (non-object entries
 * skipped, non-finite numbers coerced to 0 / null).
 */
export function summarizeWorkforce(
  workforce: Record<string, WorkforceEntry> | undefined | null,
): WorkforceRoleSummary[] {
  if (!workforce || typeof workforce !== "object") return [];
  const summaries: WorkforceRoleSummary[] = [];
  for (const [key, raw] of Object.entries(
    workforce as Record<string, unknown>,
  )) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
    const entry = raw as WorkforceEntry;
    const trimmedLabel =
      typeof entry.label === "string" && entry.label.trim().length > 0
        ? entry.label.trim()
        : undefined;
    const rawSubs: unknown[] = Array.isArray(entry.sub_items)
      ? entry.sub_items
      : [];
    const subItems: WorkforceSubItemSummary[] = rawSubs
      .filter(
        (sub): sub is Record<string, unknown> =>
          !!sub && typeof sub === "object" && !Array.isArray(sub),
      )
      .map((sub) => ({
        label: typeof sub.label === "string" ? sub.label : "",
        ftes: finiteOrZero(sub.ftes),
        cost:
          typeof sub.avg_fully_loaded_cost === "number" &&
          Number.isFinite(sub.avg_fully_loaded_cost)
            ? sub.avg_fully_loaded_cost
            : null,
      }));
    const ftes = finiteOrZero(entry.ftes);
    const cost = finiteOrZero(entry.avg_fully_loaded_cost);
    if (isBlankTemplateRole(key, entry, trimmedLabel, ftes, cost, subItems)) {
      continue;
    }
    summaries.push({
      key,
      label: trimmedLabel ?? getWorkforceRoleLabel(key),
      group: entry.group ?? getWorkforceRoleGroup(key),
      custom: entry.custom === true,
      ftes,
      cost,
      ...(typeof entry.overtime_pct === "number" &&
      Number.isFinite(entry.overtime_pct)
        ? { overtimePct: entry.overtime_pct }
        : {}),
      subItems,
    });
  }
  summaries.sort((a, b) => {
    const groupA = GROUP_ORDER_INDEX.get(a.group) ?? Number.MAX_SAFE_INTEGER;
    const groupB = GROUP_ORDER_INDEX.get(b.group) ?? Number.MAX_SAFE_INTEGER;
    if (groupA !== groupB) return groupA - groupB;
    const roleA = ROLE_ORDER_INDEX.get(a.key) ?? Number.MAX_SAFE_INTEGER;
    const roleB = ROLE_ORDER_INDEX.get(b.key) ?? Number.MAX_SAFE_INTEGER;
    if (roleA !== roleB) return roleA - roleB;
    return a.key.localeCompare(b.key);
  });
  return summaries;
}

/**
 * Prose suffix for a role's sub-items, shared by the citation docs and the
 * legacy system prompt. "" when none; otherwise e.g. " — Cardiology 20, OB-GYN 15".
 * Sub-items are descriptive (not fact-tracked), so they carry no source tag.
 */
export function formatWorkforceSubItemsSuffix(
  subItems: ReadonlyArray<{ label: string; ftes: number }>,
): string {
  if (!subItems || subItems.length === 0) return "";
  const parts = subItems
    .filter((sub) => sub.label.trim().length > 0)
    .map((sub) => `${sub.label} ${sub.ftes.toLocaleString()}`);
  return parts.length > 0 ? ` — ${parts.join(", ")}` : "";
}
